<template>
    <div class="report__wrap">
        <div class="content__page h--100 relative min-h-inherit">
            <template v-if="pageLoading">
                <PageSpinner />
            </template>
    
            <template v-else-if="pageLoadingErr">
                <EmptyPage state="error" @action="handleFetchReports" />
            </template>

            <template v-else>
                <EmptyPage 
                    v-if="accountPlan === 'free'"
                    state="empty_custom" 
                    icon-name="money" 
                    btn-state="custom"
                    type="data"
                    title="This is a paid feature"
                    subtitle="Upgrade to a paid plan to get access to this feature" 
                >
                    <template #button>
                        <BaseButton 
                            label="Go to account settings" 
                            :show-spinner="false"
                            @submit="goToAccountSettings"
                        />
                    </template>
                </EmptyPage>
                
                <div v-else>
                    <div class="flex align-items-center justify-content-between mb--15 mt--10">
                        <div class="grid gap-1">
                            <h5 class="mb--0 text-gray-700 text--semi--bold">
                                Welcome <span class="text-gray-600 text--normal">{{ profile.fullName }},</span>
                            </h5>
                            <p class="text-gray-600 text--sm">
                                Here is a quick overview of your performance in reports.
                            </p>
                        </div>

                        <div class="flex align-items-center justify-content-end gap-2">
                            <DateRangeFilter page="reports" />
                            <MainTwo page-type="reports" />
                            
                            <BaseButton
                                v-if="!isReportsEmpty"
                                variant="outline" 
                                label="Download report" 
                                prefix-icon 
                                icon-name="download" 
                                icon-width="16px"
                                variant-style="secondary" 
                                :loading="downloadLoading"
                                class="ml--5"
                                @submit="downloadReports"
                            />     
                        </div>
                    </div>
        
                    <div class="report__item__wrap">
                        <div v-if="reportFilter.type === 'clients'">
                            <ClientReports />
                        </div>
        
                        <div v-else-if="reportFilter.type === 'projects'">
                            <ProjectReports />
                        </div>
        
                        <div v-else-if="reportFilter.type === 'invoices'">
                            <InvoiceReports />
                        </div>
                    </div> 
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { EmptyPage, PageSpinner, BaseButton, DateRangeFilter } from "@/components/ui/index";
import ClientReports from "@/components/reports/ClientReports.vue";
import ProjectReports from "@/components/reports/ProjectReports.vue";
import InvoiceReports from "@/components/reports/InvoiceReports.vue";
import FormLabel from "@/components/ui/forms/FormLabel.vue";
import MainTwo from "@/components/ui/filter/MainTwo.vue";

export default {
    name: "ReportsPage",

    components: {
        ClientReports,
        EmptyPage, 
        ProjectReports,
        PageSpinner, 
        BaseButton,
        InvoiceReports,
        FormLabel,
        MainTwo,
        DateRangeFilter,
    },

    data() {
        return {
            pageLoading: false,
            pageLoadingErr: false,
            reportComponent: null,
            downloadLoading: false,
            reports: {},
        };
    },

    computed: {
        reportFilter() {
            return this.$store.getters["report/reportFilter"];
        },

        isReportsEmpty() {            
            return Object.values(this.reports).every(x => !x.length);
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        profile() {
            return this.$store.getters["auth/profile"];
        },
    },

    watch: {
        // "$route.query": "handleFetchReports",
        "$route.query": {
            async handler(newVal) {
                console.log({ newVal });
                
                if (this.accountPlan !== "free") {
                    this.setReportFilter(Object.keys(newVal).length ? newVal : { type: "clients" });
                    await this.handleFetchReports({ refresh: true, ...newVal });
                }
            },
            // immediate: true,
            deep: true,
        },
    },

    async mounted() {
        if (this.accountPlan !== "free") {
            this.setReportFilter(Object.keys(this.$route.query).length ? this.$route.query : { type: "clients" });
            await this.handleFetchReports(this.$route.query);
        }
    },

    methods: {
        goToAccountSettings() {
            this.$router.push("/dashboard/settings/payments-and-billings");
        },

        async setReportFilter(params) {
            this.$store.dispatch("report/setReportFilter", params);
        }, 

        async handleFetchReports(params) {
            try {      
                console.log({ handleFetchReports: params });
                          
                this.pageLoadingErr = false;
                this.pageLoading = true;

                const payload = {
                    fieldType: params?.fieldType || "clients",
                    range: params?.range || "1_year",
                    ...params,
                };

                if (params?.startDate) {
                    payload.startDate = params.startDate;
                }

                if (params?.endDate) {
                    payload.endDate = params.endDate;
                }

                this.reports = await this.$store.dispatch("report/fetchReports", { refresh: true, payload });
            } catch (error) {
                this.pageLoadingErr = true;
            } finally {
                this.pageLoading = false;
            }
        },

        async downloadReports(params) {
            try {                
                this.downloadLoading = true;

                const payload = {
                    type: this.$route.query?.type || "clients",
                    range: this.$route.query?.range || "1_year",
                    download: true,
                };

                if (params?.startDate) {
                    payload.startDate = params.startDate;
                }

                if (params?.endDate) {
                    payload.endDate = params.endDate;
                }

                const resp = await this.$store.dispatch("report/downloadReports", { 
                    payload, 
                    config: {
                        responseType: "blob", // Important for handling binary data,
                        headers: {
                            "Accept": "text/csv",
                        },
                    },
                });

                const url = window.URL.createObjectURL(new Blob([resp], { type: "text/csv" }));
                const link = document.createElement("a");
    
                link.href = url;
                link.setAttribute("download", `${this.reportFilter?.type || "user"}_reports.csv`); // or the filename you want
                    
                document.body.appendChild(link);
                link.click();
    
                URL.revokeObjectURL(url);
                document.body.removeChild(link);
                
                this.$toast.success("Report downloaded");
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                console.log({ errMsg });
                
                this.$toast.error(errMsg);
            } finally {
                this.downloadLoading = false;
            }
        },
    },
};
</script>